import { getCountryFormDefinition } from "CMS/utils";
import { CanReceivePeppol } from "company/detail/components/CanReceivePeppol";
import CompanyGoogleMap from "company/detail/components/CompanyGoogleMap";
import { FinancialRiskEdit } from "company/detail/components/FinancialRiskEdit";
import { PostalAndVisitingAddress } from "company/detail/components/models";
import { PostalAddressSidePanel } from "company/detail/components/PostalAddress";
import { PostalAddressForm } from "company/detail/components/PostalAddressForm";
import { VisitingAddressForm } from "company/detail/components/VisitingAddressForm";
import { convertToBankFormState, convertToFormState, getInstruction } from "company/detail/utils";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import { DeletedRow } from "components/DetailPage/DeletedRow";
import NotValidatedRow from "components/DetailPage/NotValidatedRow";
import PhoneRow from "components/DetailPage/PhoneRow";
import StatusDropdown from "components/dropdowns/reactselect/StatusDropdown";
import FinancialRiskCard from "components/FinancialRisk/FinancialRiskCard";
import { ViewOnMap } from "components/GoogleMap/ViewOnMap";
import { SidePanelDetailSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelDetailSectionTemplate";
import { convertToPhoneNumbers, PhoneInput } from "components/PhoneInput";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import Status from "components/Status/Status";
import { useHasAnyTags } from "components/Tag/hooks";
import TagList from "components/Tag/TagList";
import TagsSelect from "components/Tag/TagsSelect";
import UrlInput from "components/urlinput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import VatNumberDetails from "components/VatNumber/VatNumberDetails";
import WebsiteLink from "components/Website/WebsiteLink";
import React from "react";
import { isNullOrEmptyString } from "utilities";
import { countrySupportsPeppol } from "utilities/eInvoiceUtils";
import { useQueryClient } from "@tanstack/react-query";
import { CompanyApi } from "queries/Company";
import { GlobalLocationNumberInput } from "components/ValidatedFields/GlobalLocationNumberInput";


interface CompanySidePanelDetailsProps {
  company: server.dto.CompanySidePanelData
  viewOnMap?: boolean
  refetch: () => Promise<void>
  onChange?: () => void
}

export const CompanySidePanelDetails = (props: CompanySidePanelDetailsProps) => {
  const companyData = props.company;
  const [companyFormState, setCompanyFormState] = React.useState(convertToFormState(companyData.company));
  const bankFormState = convertToBankFormState(companyData.company.bank);
  const [tags, setTags] = React.useState(companyData.company.tags || []);

  const countryFormDefinition = getCountryFormDefinition(companyData.company.admAddress.country);

  const onValueChanged = (key: string, value: any) => {
    if (key === "visitingAddress" || key === "postalAddress") {
      setCompanyFormState(prevState => ({
        ...prevState,
        address: value,
      }));
    } else if (key === "phone") {
      setCompanyFormState(prevState => ({
        ...prevState,
        phone: value,
      }));
    } else if (key === "website") {
      setCompanyFormState(prevState => ({
        ...prevState,
        website: value,
      }));
    } else if (key === "facebook") {
      setCompanyFormState(prevState => ({
        ...prevState,
        facebook: value,
      }));
    } else if (key === "euVatNumber") {
      setCompanyFormState(prevState => ({
        ...prevState,
        euVatNumber: value,
      }));
    } else if (key === "globalLocationNumber") {
      setCompanyFormState(prevState => ({
        ...prevState,
        globalLocationNumber: value,
      }));
    } else if (key === "fiscalNumber1") {
      setCompanyFormState(prevState => ({
        ...prevState,
        fiscalNumber1: value,
      }));
    } else if (key === "fiscalNumber2") {
      setCompanyFormState(prevState => ({
        ...prevState,
        fiscalNumber2: value,
      }));
    } else if (key === "businessRegistrationNumber") {
      setCompanyFormState(prevState => ({
        ...prevState,
        businessRegistrationNumber: value,
      }));
    } else if (key === "internalId") {
      setCompanyFormState(prevState => ({
        ...prevState,
        internalId: value,
      }));
    } else if (key === "financialRisk") {
      setCompanyFormState(prevState => ({
        ...prevState,
        financialRisk: value,
      }));
    } else if (key === "status") {
      setCompanyFormState(prevState => ({
        ...prevState,
        status: value,
      }));
    } else if (key === "tags") {
      setTags(value);
    } else if (key === "owner") {
      setCompanyFormState(prevCompany => ({
        ...prevCompany,
        owner: value,
      }));
    }
  }

  const hasMultipleStatuses = site.currentEnvironment.statuses.company.length > 1;
  const hasAnyTags = useHasAnyTags("company");
  const hasLandlineNumbers = !isNullOrEmptyString(companyFormState.phone);
  const canInPageEdit = companyData.company.permissions.canModify;

  const queryClient = useQueryClient();

  const onSuccessfulUpdate = async () => {
    queryClient.invalidateQueries(CompanyApi.CanReceivePeppol.getCacheKey(companyData.company.companyId));
    await props.refetch();
    props.onChange?.();
  }

  return <SidePanelDetailSectionTemplate
    isInPageEditable={canInPageEdit}
    onSuccessfulUpdate={onSuccessfulUpdate}
    onValueChanged={onValueChanged}
    fields={[{
      key: "isDeleted",
      label: "",
      hideLabel: true,
      hidden: !companyData.company.isDeleted,
      render: () => <DeletedRow />
    }, {
      key: "isValidated",
      label: "",
      hideLabel: true,
      hidden: companyData.company.isValidated,
      render: () => <NotValidatedRow
        canApprove={canInPageEdit}
        sublabel={RESX.Planning.InputFromWebform}
        approveEndpoint={() => window.CoreApi.Company.Validate({
          companyId: companyData.company.companyId
        })} />
    }, {
      key: "visitingAddress",
      label: RESX.Company.VisitingAddress,
      render: () => <>
        <div>{companyData.company.name}</div>
        <div className="text-sublabel">{companyData.company.address.formatted.join(", ")}</div>
      </>,
      inPageEdit: {
        useCustomSavingState: true,
        fullWidth: true,
        render: ({ value, onChange, submit, isSaving }) => <VisitingAddressForm
          noLeftPadding
          submit={submit}
          onChange={onChange}
          value={value}
          isSaving={isSaving}
        />,
        initialValue: companyFormState.address,
        type: "custom",
        submitChange: (value: PostalAndVisitingAddress) => {
          const instruction = getInstruction(companyData.company, companyFormState, bankFormState, {
            regularName: value.visitingAddress.companyName,
            regularAddress: {
              addressLine1: value.visitingAddress.addressLine1,
              addressLine2: value.visitingAddress.addressLine2,
              city: value.visitingAddress.city,
              postalCode: value.visitingAddress.postalCode,
              stateId: value.visitingAddress.state.key,
            },
            administrativeName: value.postalAddress.companyName,
            administrativeAddress: {
              addressLine1: value.postalAddress.addressLine1,
              addressLine2: value.postalAddress.addressLine2,
              city: value.postalAddress.city,
              postalCode: value.postalAddress.postalCode,
              stateId: value.postalAddress.state.key,
            }
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "postalAddress",
      label: RESX.Company.PostalAddress,
      render: () => <PostalAddressSidePanel company={companyData.company} />,
      inPageEdit: {
        useCustomSavingState: true,
        fullWidth: true,
        render: ({ value, onChange, submit, isSaving }) => <PostalAddressForm
          noLeftPadding
          value={value}
          onChange={onChange}
          submit={submit}
          isSaving={isSaving}
        />,
        initialValue: companyFormState.address,
        type: "custom",
        submitChange: (value: PostalAndVisitingAddress) => {
          const instruction = getInstruction(companyData.company, companyFormState, bankFormState, {
            regularName: value.visitingAddress.companyName,
            regularAddress: {
              addressLine1: value.visitingAddress.addressLine1,
              addressLine2: value.visitingAddress.addressLine2,
              city: value.visitingAddress.city,
              postalCode: value.visitingAddress.postalCode,
              stateId: value.visitingAddress.state.key,
            },
            administrativeName: value.postalAddress.companyName,
            administrativeAddress: {
              addressLine1: value.postalAddress.addressLine1,
              addressLine2: value.postalAddress.addressLine2,
              city: value.postalAddress.city,
              postalCode: value.postalAddress.postalCode,
              stateId: value.postalAddress.state.key,
            }
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "phone",
      label: RESX.Company.Phone,
      render: () => <PhoneRow numbers={convertToPhoneNumbers(companyFormState.phone)} />,
      isEmpty: !hasLandlineNumbers,
      inPageEdit: {
        fullWidth: true,
        render: ({ value, onChange, submit, cancel }) => (
          <PhoneInput
            focusOnMount
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: companyFormState.phone,
        submitChange: (value: string) => {
          const instruction = getInstruction(companyData.company, companyFormState, bankFormState, {
            phoneNumbers: convertToPhoneNumbers(value),
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "website",
      label: RESX.GeneralLabels.Website,
      render: () => <WebsiteLink website={companyFormState.website} />,
      isEmpty: !companyFormState.website,
      inPageEdit: {
        render: ({ value, onChange, cancel, submit }) => (
          <UrlInput
            focusOnMount
            value={value}
            onValueChange={onChange}
            validatorKey="companyWebsite"
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.url.maxUrlLength}
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: companyFormState.website,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(companyData.company, companyFormState, bankFormState, {
            website: value,
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "facebook",
      label: "Facebook",
      render: () => <WebsiteLink website={companyFormState.facebook} />,
      isEmpty: !companyFormState.facebook,
      inPageEdit: {
        render: ({ value, onChange, cancel, submit }) => (
          <UrlInput
            focusOnMount
            value={value}
            onValueChange={onChange}
            validatorKey="companyFacebook"
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.url.maxFacebookLength}
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: companyFormState.facebook,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(companyData.company, companyFormState, bankFormState, {
            facebook: value,
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "euVatNumber",
      hidden: !countryFormDefinition.euVatNumber.display,
      label: countryFormDefinition.euVatNumber.label,
      sublabel: countryFormDefinition.euVatNumber.sublabel,
      render: () => <VatNumberDetails companyId={companyData.company.companyId} euVatNumber={companyData.company.euVatNumber} />,
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.euVatNumber,
        useCustomSavingState: true,
        render: ({ value, onChange, submit, cancel, isSaving }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.company.maxEuVatNumberLength}
            onEnter={submit}
            onEsc={cancel}
            loading={isSaving}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getInstruction(companyData.company, companyFormState, bankFormState, { euVatNumber: value });
          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      },
    }, {
      key: "fiscalNumber1",
      hidden: !countryFormDefinition.fiscalNumber1.display,
      label: countryFormDefinition.fiscalNumber1.label,
      sublabel: countryFormDefinition.fiscalNumber1.sublabel,
      render: () => <div>{companyFormState.fiscalNumber1}</div>,
      isEmpty: isNullOrEmptyString(companyFormState.fiscalNumber1),
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.fiscalNumber1,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.company.maxFiscalNumber1Length}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getInstruction(companyData.company, companyFormState, bankFormState, { fiscalNumber1: value });
          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      },
    }, {
      key: "fiscalNumber2",
      hidden: !countryFormDefinition.fiscalNumber2.display,
      label: countryFormDefinition.fiscalNumber2.label,
      sublabel: countryFormDefinition.fiscalNumber2.sublabel,
      render: () => <div>{companyFormState.fiscalNumber2}</div>,
      isEmpty: isNullOrEmptyString(companyFormState.fiscalNumber2),
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.fiscalNumber2,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.company.maxFiscalNumber2Length}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getInstruction(companyData.company, companyFormState, bankFormState, { fiscalNumber2: value });
          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "businessRegistrationNumber",
      hidden: !countryFormDefinition.businessRegistrationNumber.display,
      label: countryFormDefinition.businessRegistrationNumber.label,
      sublabel: countryFormDefinition.businessRegistrationNumber.sublabel,
      render: () => <div>{companyFormState.businessRegistrationNumber}</div>,
      isEmpty: isNullOrEmptyString(companyFormState.businessRegistrationNumber),
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.businessRegistrationNumber,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.company.maxBusinessRegistrationNumberLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getInstruction(companyData.company, companyFormState, bankFormState, { businessRegistrationNumber: value });
          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      },
    }, {
      key: "globalLocationNumber",
      label: "Global Location Number (GLN)",
      render: () => <div>{companyFormState.globalLocationNumber}</div>,
      isEmpty: isNullOrEmptyString(companyFormState.globalLocationNumber),
      inPageEdit: {
        initialValue: companyFormState.globalLocationNumber,
        fullWidth: true,
        render: ({ value, onChange, submit, cancel }) => (
          <GlobalLocationNumberInput
            focusOnMount
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value) => {
          const instruction = getInstruction(companyData.company, companyFormState, bankFormState, { globalLocationNumber: value });
          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "internalId",
      label: RESX.Company.CustomerNumber,
      render: () => <div>{companyFormState.internalId}</div>,
      isEmpty: isNullOrEmptyString(companyFormState.internalId),
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.internalId,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.company.maxInternalIdLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getInstruction(companyData.company, companyFormState, bankFormState, { internalId: value });
          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      },
    }, {
      key: "peppolCompliance",
      label: "Peppol",
      hidden: !countrySupportsPeppol(companyData.company.admAddress.country),
      render: () => <CanReceivePeppol companyId={companyData.company.companyId} />,
    }, {
      key: "financialRisk",
      label: RESX.Promoter.FinancialRisk,
      render: () => <FinancialRiskCard financialRisk={companyFormState.financialRisk} />,
      isEmpty: companyFormState.financialRisk.level.value === "unknown",
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.financialRisk,
        render: ({ value, onChange }) => (
          <FinancialRiskEdit
            value={value}
            onChange={onChange}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: server.dto.FinancialRisk) => {
          const instruction = getInstruction(companyData.company, companyFormState, bankFormState, {
            financialRisk: {
              comment: value.level.value === "unknown" ? null : value.comment,
              level: value.level.value
            }
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "status",
      label: RESX.Status.resxStatus,
      render: () => <Status status={companyFormState.status} />,
      hidden: !hasMultipleStatuses,
      inPageEdit: {
        render: ({ value, onChange }) => (
          <StatusDropdown
            value={value}
            onChange={onChange}
            mainArea="company"
            options={undefined}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: companyFormState.status,
        fullWidth: true,
        submitChange: (value: server.dto.StatusSmall) => {
          const instruction = getInstruction(companyData.company, companyFormState, bankFormState, {
            statusId: value.statusId,
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "owner",
      label: RESX.GeneralLabels.Owner,
      render: () => <UserWithAvatar user={companyFormState.owner} />,
      isEmpty: !companyFormState.owner,
      inPageEdit: {
        render: ({ onChange }) => (
          <div className="main-section-template-fields-padding">
            <UserModalPicker
              value={undefined}
              onChange={v => onChange(v, true)}
              required={false}
              validatorKey="companyOwner"
              canCreateNew={false}
            />
          </div>
        ),
        type: "custom",
        initialValue: companyFormState.owner,
        submitChange: (value: server.dto.UserSmall) => {
          const instruction = getInstruction(companyData.company, companyFormState, bankFormState, {
            ownerUserId: value.userId,
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "tags",
      label: RESX.Tag.Tags,
      hidden: !hasAnyTags,
      isEmpty: tags.length === 0,
      render: () => <TagList tags={tags} length="short" />,
      inPageEdit: {
        render: ({ value, onChange }) => (
          <div className="main-section-template-fields-padding">
            <TagsSelect
              mainArea="company"
              value={value}
              onChange={onChange}
            />
          </div>
        ),
        type: "confirmOrCancelButton",
        initialValue: tags,
        submitChange: (tags: server.dto.TagSmall[]) => {
          return window.CoreApi.Company.UpdateTags({
            itemId: companyData.company.companyId,
            tagsIds: tags.map(tag => tag.tagId),
          });
        }
      }
    }]}
    additionalContent={<>
      {props.viewOnMap && <>
        <ViewOnMap height={400}>
          <CompanyGoogleMap company={companyData.company} />
        </ViewOnMap>
      </>}
    </>}
  />
}